import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Plus,
  Trash2,
  ExternalLink,
  Github,
  Eye,
  EyeOff,
  Star,
  Loader2,
  FolderOpen,
  Upload,
} from "lucide-react";
import { EmptyState } from "@/components/shared/EmptyState";
import { ConceptBadge } from "@/components/shared/ConceptBadge";
import {
  buildCaseStudy,
  caseStudyToForm,
  isConceptProject,
  slugify,
  type CaseStudyForm,
} from "@/lib/caseStudy";
import { useImageUpload } from "@/hooks/useImageUpload";
import { safeHttpUrl } from "@/lib/safeUrl";
import {
  useProjects,
  useCreateProject,
  useUpdateProject,
  useDeleteProject,
  useToggleProjectStatus,
  useToggleProjectFeatured,
  type Project,
  type ProjectStatus,
} from "@/hooks/useProjects";

interface ProjectFormData {
  title: string;
  description: string;
  image_url: string;
  technologies: string;
  project_url: string;
  github_url: string;
  featured: boolean;
  status: ProjectStatus;
  slug: string;
  display_order: string;
  caseStudy: CaseStudyForm;
}

const defaultFormData: ProjectFormData = {
  title: "",
  description: "",
  image_url: "",
  technologies: "",
  project_url: "",
  github_url: "",
  featured: false,
  status: "draft",
  slug: "",
  display_order: "0",
  caseStudy: caseStudyToForm(null),
};

interface ProjectManagerProps {
  /** Rendered inside ContentHub — hides the standalone header. */
  embedded?: boolean;
}

export function ProjectManager({ embedded = false }: ProjectManagerProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<string | null>(null);
  const [formData, setFormData] = useState<ProjectFormData>(defaultFormData);
  // New cases get a slug that follows the title until the admin edits it.
  const [slugTouched, setSlugTouched] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { uploadImage, uploading } = useImageUpload();

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = await uploadImage(file);
    if (url) setFormData((prev) => ({ ...prev, image_url: url }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const { data: projects, isLoading } = useProjects();
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const deleteProject = useDeleteProject();
  const toggleStatus = useToggleProjectStatus();
  const toggleFeatured = useToggleProjectFeatured();

  const handleOpenDialog = (project?: Project) => {
    if (project) {
      setEditingProject(project);
      setFormData({
        title: project.title,
        description: project.description,
        image_url: project.image_url || "",
        technologies: project.technologies?.join(", ") || "",
        project_url: project.project_url || "",
        github_url: project.github_url || "",
        featured: project.featured || false,
        status: project.status || "draft",
        slug: project.slug || "",
        display_order: String(project.display_order ?? 0),
        caseStudy: caseStudyToForm(project.case_study),
      });
      setSlugTouched(true);
    } else {
      setEditingProject(null);
      setFormData(defaultFormData);
      setSlugTouched(false);
    }
    setDialogOpen(true);
  };

  const setCaseStudy = (patch: Partial<CaseStudyForm>) =>
    setFormData((prev) => ({ ...prev, caseStudy: { ...prev.caseStudy, ...patch } }));

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingProject(null);
    setFormData(defaultFormData);
  };

  const handleSubmit = async () => {
    if (!formData.title.trim() || !formData.description.trim()) return;

    const projectData = {
      title: formData.title.trim(),
      description: formData.description.trim(),
      image_url: formData.image_url.trim() || undefined,
      technologies: formData.technologies
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      project_url: formData.project_url.trim() || undefined,
      github_url: formData.github_url.trim() || undefined,
      featured: formData.featured,
      status: formData.status,
      slug: slugify(formData.slug) || null,
      display_order: Number.parseInt(formData.display_order, 10) || 0,
      case_study: buildCaseStudy(formData.caseStudy, editingProject?.case_study),
    };

    if (editingProject) {
      await updateProject.mutateAsync({ id: editingProject.id, data: projectData });
    } else {
      await createProject.mutateAsync(projectData);
    }
    handleCloseDialog();
  };

  const handleDelete = (id: string) => {
    setProjectToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (projectToDelete) {
      await deleteProject.mutateAsync(projectToDelete);
      setDeleteDialogOpen(false);
      setProjectToDelete(null);
    }
  };

  const isPending = createProject.isPending || updateProject.isPending;

  return (
    <div className="flex flex-col gap-[22px]">
      <div className="flex flex-wrap items-end justify-between gap-3">
        {!embedded ? (
          <div>
            <h1 className="mb-1.5 font-display text-[27px] font-bold tracking-[-0.03em]">Cases</h1>
            <p className="text-[13.5px] text-muted-foreground">
              Cases do portfólio exibidos em /projetos
            </p>
          </div>
        ) : (
          <span />
        )}
        <Button
          className="h-8 rounded-lg px-[13px] text-xs font-semibold"
          onClick={() => handleOpenDialog()}
        >
          Novo case
        </Button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : !projects || projects.length === 0 ? (
        <EmptyState
          icon={<FolderOpen className="h-5 w-5" />}
          title="Nenhum case cadastrado."
          className="rounded-2xl border border-border bg-card py-12 shadow-card"
          action={
            <Button onClick={() => handleOpenDialog()}>
              <Plus className="mr-2 h-4 w-4" />
              Criar primeiro case
            </Button>
          }
        />
      ) : (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-4">
          {projects.map((project) => (
            <div
              key={project.id}
              className="flex flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-card"
            >
              <div className="flex h-[130px] items-center justify-center overflow-hidden border-b border-border bg-muted font-mono text-[11px] text-muted-foreground">
                {project.image_url ? (
                  <img
                    src={project.image_url}
                    alt={project.title}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  "imagem do case"
                )}
              </div>
              <div className="flex flex-1 flex-col px-5 py-[18px]">
                <div className="flex items-center justify-between gap-2.5">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="truncate text-[14.5px] font-bold">{project.title}</span>
                    {isConceptProject(project) && <ConceptBadge />}
                  </span>
                  <span
                    className={`inline-flex h-[22px] items-center whitespace-nowrap rounded-[7px] px-[9px] text-[10.5px] font-bold ${project.status === "published" ? "bg-ok-soft text-ok" : "bg-muted text-muted-foreground"}`}
                  >
                    {project.status === "published" ? "Publicado" : "Rascunho"}
                  </span>
                </div>
                <p className="mt-[7px] line-clamp-2 text-[12.5px] leading-[1.5] text-muted-foreground">
                  {project.description}
                </p>
                {project.technologies && project.technologies.length > 0 && (
                  <div className="mt-3.5 truncate font-mono text-[11.5px] text-muted-foreground">
                    {project.technologies.join(" · ")}
                  </div>
                )}
                <div className="mt-auto flex items-center justify-between pt-3.5">
                  <div className="flex gap-0.5">
                    {safeHttpUrl(project.project_url) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() =>
                          window.open(safeHttpUrl(project.project_url)!, "_blank", "noopener")
                        }
                        aria-label="Ver site do case"
                      >
                        <ExternalLink className="h-3 w-3" />
                      </Button>
                    )}
                    {safeHttpUrl(project.github_url) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7"
                        onClick={() =>
                          window.open(safeHttpUrl(project.github_url)!, "_blank", "noopener")
                        }
                        aria-label="Ver repositório no GitHub"
                      >
                        <Github className="h-3 w-3" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() =>
                        toggleFeatured.mutate({ id: project.id, currentFeatured: project.featured })
                      }
                      aria-label={project.featured ? "Remover destaque" : "Marcar como destaque"}
                    >
                      <Star
                        className={`h-3 w-3 ${project.featured ? "fill-warn text-warn" : ""}`}
                      />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() =>
                        toggleStatus.mutate({ id: project.id, currentStatus: project.status })
                      }
                      aria-label={project.status === "published" ? "Despublicar" : "Publicar"}
                    >
                      {project.status === "published" ? (
                        <EyeOff className="h-3 w-3" />
                      ) : (
                        <Eye className="h-3 w-3" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 text-bad hover:text-bad"
                      onClick={() => handleDelete(project.id)}
                      aria-label="Excluir case"
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                  <Button
                    variant="outline"
                    className="h-[31px] rounded-lg px-[13px] text-xs font-semibold"
                    onClick={() => handleOpenDialog(project)}
                  >
                    Editar
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingProject ? "Editar case" : "Novo case"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="title">Título *</Label>
                <Input
                  id="title"
                  placeholder="Nome do case"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      title: e.target.value,
                      slug: slugTouched ? formData.slug : slugify(e.target.value),
                    })
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) =>
                    setFormData({ ...formData, status: value as ProjectStatus })
                  }
                >
                  <SelectTrigger id="status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Rascunho</SelectItem>
                    <SelectItem value="published">Publicado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Descrição *</Label>
              <Textarea
                id="description"
                placeholder="Descreva o case..."
                rows={3}
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="image_url">Imagem de capa</Label>
              <div className="flex gap-2">
                <Input
                  id="image_url"
                  placeholder="https://... ou envie um arquivo"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                />
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Upload className="mr-2 h-4 w-4" />
                      Enviar
                    </>
                  )}
                </Button>
              </div>
              {formData.image_url && (
                <img
                  src={formData.image_url}
                  alt="Preview"
                  className="mt-2 max-h-40 rounded-md border border-border object-cover"
                />
              )}
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="project_url">URL do Site</Label>
                <Input
                  id="project_url"
                  placeholder="https://..."
                  value={formData.project_url}
                  onChange={(e) => setFormData({ ...formData, project_url: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="github_url">URL do GitHub</Label>
                <Input
                  id="github_url"
                  placeholder="https://github.com/..."
                  value={formData.github_url}
                  onChange={(e) => setFormData({ ...formData, github_url: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="technologies">Tecnologias (separadas por vírgula)</Label>
              <Input
                id="technologies"
                placeholder="React, TypeScript, Tailwind..."
                value={formData.technologies}
                onChange={(e) => setFormData({ ...formData, technologies: e.target.value })}
              />
            </div>

            <div className="grid gap-4 md:grid-cols-[1fr_120px]">
              <div className="space-y-2">
                <Label htmlFor="slug">Slug</Label>
                <Input
                  id="slug"
                  placeholder="forno-da-vila"
                  value={formData.slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setFormData({ ...formData, slug: e.target.value });
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="display_order">Ordem</Label>
                <Input
                  id="display_order"
                  type="number"
                  inputMode="numeric"
                  value={formData.display_order}
                  onChange={(e) => setFormData({ ...formData, display_order: e.target.value })}
                />
              </div>
            </div>

            <fieldset className="space-y-4 rounded-xl border border-border p-4">
              <legend className="px-1 text-[12.5px] font-semibold text-muted-foreground">
                Estudo de caso
              </legend>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label htmlFor="cs-sector">Setor</Label>
                  <Input
                    id="cs-sector"
                    placeholder="Padaria artesanal"
                    value={formData.caseStudy.sector}
                    onChange={(e) => setCaseStudy({ sector: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cs-metric">Métrica</Label>
                  <Input
                    id="cs-metric"
                    placeholder="98"
                    value={formData.caseStudy.metric}
                    onChange={(e) => setCaseStudy({ metric: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cs-metric-label">Rótulo da métrica</Label>
                  <Input
                    id="cs-metric-label"
                    placeholder="Lighthouse performance"
                    value={formData.caseStudy.metricLabel}
                    onChange={(e) => setCaseStudy({ metricLabel: e.target.value })}
                  />
                </div>
              </div>
              {(
                [
                  ["challenge", "Desafio"],
                  ["solution", "Solução"],
                  ["results", "Resultados"],
                ] as const
              ).map(([key, label]) => (
                <div key={key} className="space-y-2">
                  <Label htmlFor={`cs-${key}`}>{label}</Label>
                  <Textarea
                    id={`cs-${key}`}
                    rows={3}
                    value={formData.caseStudy[key]}
                    onChange={(e) => setCaseStudy({ [key]: e.target.value })}
                  />
                </div>
              ))}
              <div className="space-y-2">
                <Label htmlFor="cs-gallery">Galeria (uma URL por linha)</Label>
                <Textarea
                  id="cs-gallery"
                  rows={3}
                  placeholder={"https://...\n/demos/meu-case/brand-board.webp"}
                  className="font-mono text-xs"
                  value={formData.caseStudy.gallery}
                  onChange={(e) => setCaseStudy({ gallery: e.target.value })}
                />
              </div>
              <div className="flex items-start space-x-2">
                <Switch
                  id="cs-concept"
                  checked={formData.caseStudy.concept}
                  onCheckedChange={(checked) => setCaseStudy({ concept: checked })}
                />
                <div className="space-y-0.5">
                  <Label htmlFor="cs-concept">Projeto conceito</Label>
                  <p className="text-[12px] text-muted-foreground">
                    Marca fictícia criada como demonstração. Exibe o selo &quot;Conceito&quot; e o
                    aviso no site.
                  </p>
                </div>
              </div>
            </fieldset>

            <div className="flex items-center space-x-2">
              <Switch
                id="featured"
                checked={formData.featured}
                onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
              />
              <Label htmlFor="featured">Case em destaque</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseDialog}>
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={isPending || !formData.title.trim() || !formData.description.trim()}
            >
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editingProject ? "Salvar Alterações" : "Criar case"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir case</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este case? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
